import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { AllieSheetProps } from './Sheet.types';
import {
  SheetBackdrop,
  SheetContainer,
  SheetHandle,
  SheetHandleBar,
  SheetContent,
} from './Sheet.styles';
import { useSheetGestures } from './useSheetGestures';

export const AllieSheet: FC<AllieSheetProps> = ({
  isOpen,
  onClose,
  children,
  showHandle = true,
  maxHeight = '50dvh',
  showBackdrop = true,
  style,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  // Gestionar visibilidad del portal
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, isVisible]);

  // Animar entrada/salida del sheet
  useEffect(() => {
    if (!sheetRef.current) return;

    if (isOpen && isVisible) {
      // Animar entrada
      gsap.to(sheetRef.current, {
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
      });
    } else if (isClosing) {
      // Animar salida
      gsap.to(sheetRef.current, {
        y: '100%',
        duration: 0.3,
        ease: 'power2.in',
      });
    }
  }, [isOpen, isVisible, isClosing]);

  // Hook de gestos
  useSheetGestures(sheetRef, handleRef, { onClose, isOpen });

  // Prevenir scroll del body cuando el sheet está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isVisible) return null;

  const modalContent = (
    <>
      {showBackdrop && <SheetBackdrop isClosing={isClosing} onClick={onClose} />}
      <SheetContainer
        ref={sheetRef}
        maxHeight={maxHeight}
        style={style}
        className={className}
      >
        {showHandle && (
          <SheetHandle ref={handleRef}>
            <SheetHandleBar />
          </SheetHandle>
        )}
        <SheetContent>{children}</SheetContent>
      </SheetContainer>
    </>
  );

  return createPortal(modalContent, document.body);
};
